import { Grid } from '@mui/material';
import { styles } from '~/modules/identification/forms/BusinessRelations/styles.ts';
import TableCell from '~/components/ListCardTable/TableCell.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { TableActionButtons } from '~/components/ListCardTable/TableActionButtons.tsx';
import { useCurrenciesQuery } from '~/queries/useCurrenciesQuery.ts';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import {
  TBankAccounts,
  TFinancialCondition,
} from '~/modules/identification/forms/FinancialCondition/types.ts';
import { EMPTY_BANK_ACCOUNTS } from '~/modules/identification/forms/FinancialCondition/constants.ts';
import { IBAN_MAX_LENGTH } from '~/constants/common.ts';
import { useFormContext } from 'react-hook-form';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { infoByIban } from '~/store/identification-form/thunks/thunks.ts';
import { useMemo } from 'react';

interface IBankAccountsTableProps {
  index: number;
  showRemove: boolean;
  append: (item: TBankAccounts) => void;
  remove: (index: number) => void;
}

export const BankAccountsTable = ({
  append,
  remove,
  index,
  showRemove,
}: IBankAccountsTableProps) => {
  const dispatch = useAppDispatch();
  const { currencyOptions } = useCurrenciesQuery();
  const { getValues, setValue, trigger } = useFormContext<TFinancialCondition>();
  const currency = useMemo(() => currencyOptions, [currencyOptions]);

  const fieldName = `bankAccounts.${index}.accountNumber` as const;

  const handleAccountNumber = async () => {
    const value = getValues(fieldName);
    if (value?.length === IBAN_MAX_LENGTH) {
      const normalized = value.slice(0, 2).toUpperCase() + value.slice(2);

      if (normalized !== value) {
        setValue(fieldName, normalized, { shouldValidate: true });
      }

      try {
        const iban = await dispatch(infoByIban({ data: normalized })).unwrap();

        setValue(`bankAccounts.${index}.bankMFO`, iban.bankMFO, { shouldValidate: true });
        setValue(`bankAccounts.${index}.bankName`, iban.bankName, { shouldValidate: true });
        trigger(`bankAccounts.${index}.accountNumber`);
      } catch {
        setValue(`bankAccounts.${index}.bankMFO`, null);
        setValue(`bankAccounts.${index}.bankName`, null);
        trigger(`bankAccounts.${index}.bankMFO`);
        trigger(`bankAccounts.${index}.bankName`);
        trigger(`bankAccounts.${index}.accountNumber`);
      }
    }
  };

  const handleAccountNumberBlur = () => {
    const value = getValues(fieldName);
    if (typeof value === 'string' && value.length >= 2) {
      const normalized = value.slice(0, 2).toUpperCase() + value.slice(2);
      setValue(fieldName, normalized, { shouldValidate: true });
    }
  };

  return (
    <Grid container sx={styles.row}>
      <TableCell title="Номер рахунку" size={4}>
        <FormInput
          name={`bankAccounts.${index}.accountNumber`}
          placeholder="Введіть рахунок у форматі IBAN"
          onChange={handleAccountNumber}
          variant="standard"
          fullWidth
          sx={{ ...styles.tableInput, pt: '14px' }}
          inputProps={{
            onBlur: handleAccountNumberBlur,
            minLength: 1,
            maxLength: IBAN_MAX_LENGTH,
          }}
        />
      </TableCell>
      <TableCell title="Найменування банку" size={3}>
        <FormInput
          name={`bankAccounts.${index}.bankName`}
          placeholder="Поле заповнюється автоматично"
          disabled
          variant="standard"
          fullWidth
          sx={{ ...styles.tableInput, pt: '14px' }}
          inputProps={{ minLength: 1, maxLength: 255 }}
        />
      </TableCell>
      <TableCell title="МФО банку" size={2}>
        <FormInput
          name={`bankAccounts.${index}.bankMFO`}
          placeholder="Поле заповнюється автоматично"
          disabled
          variant="standard"
          fullWidth
          sx={{ ...styles.tableInput, pt: '14px' }}
          inputProps={{ minLength: 1, maxLength: 255 }}
        />
      </TableCell>
      <TableCell title="Валюта" size={2}>
        <FormAutocomplete
          name={`bankAccounts.${index}.currency`}
          options={currency}
          inputProps={{ placeholder: 'Виберіть валюту' }}
          variant="standard"
          fullWidth
          sx={{ ...styles.tableInput, pt: '14px' }}
        />
      </TableCell>
      <TableActionButtons
        addClick={() => append(EMPTY_BANK_ACCOUNTS)}
        removeClick={() => remove(index)}
        showRemove={showRemove}
        blockName={fieldName}
      />
    </Grid>
  );
};
